import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  alpha,
  Slide,
  Backdrop,
  Box,
  Divider,
  CircularProgress,
  Switch,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  AccessibilityNew as AccessibilityNewIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Print as PrintIcon,
  VolumeUp as VolumeUpIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';

import { toast } from 'react-toastify';
import api from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

// Transição personalizada para o Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transição personalizada para o Dialog de senha emitida
const SenhaTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmitirSenhaModal = ({ open, onClose, customTheme, user, onSenhaEmitida }) => {
  const { isDarkMode } = useTheme();
  const [servicos, setServicos] = useState([]);
  const [carregandoServicos, setCarregandoServicos] = useState(false);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [senhaEmitida, setSenhaEmitida] = useState(null);
  const [dialogoSenhaAberto, setDialogoSenhaAberto] = useState(false);
  const [emitindoSenha, setEmitindoSenha] = useState(false);
  const [atualizandoDashboard, setAtualizandoDashboard] = useState(false);
  
  // Estados para configurações
  const [imprimirSenha, setImprimirSenha] = useState(false); // Padrão: não imprimir
  const [chamarNoPainel, setChamarNoPainel] = useState(false);
  const [motivoEmissao, setMotivoEmissao] = useState('');
  const [justificativaOutro, setJustificativaOutro] = useState('');

  // Estados para motivos de emissão
  const [motivosDisponiveis, setMotivosDisponiveis] = useState([
    // Fallback para os motivos hardcodados
    { id: null, nome: 'Cliente sem senha e fila zerada', requer_justificativa: false },
    { id: null, nome: 'Serviço realizado sem cliente', requer_justificativa: false },
    { id: null, nome: 'Outro', requer_justificativa: true },
  ]);
  const [carregandoMotivos, setCarregandoMotivos] = useState(false);

  // Adicionar useEffect para fechar o diálogo automaticamente
  useEffect(() => {
    let timeoutId;
    if (dialogoSenhaAberto) {
      timeoutId = setTimeout(() => {
        setDialogoSenhaAberto(false);
        // Garantir que o dashboard foi atualizado antes de fechar
        setTimeout(() => {
          handleClose(); // Fecha o modal principal também
        }, 200);
      }, 4000); // 4 segundos
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [dialogoSenhaAberto]);

  // Buscar serviços e motivos do backend quando o modal abrir
  useEffect(() => {
    if (open) {
      fetchServicos();
      fetchMotivosEmissao();
    }
  }, [open]);

  const fetchServicos = async () => {
    setCarregandoServicos(true);
    try {
      const response = await api.get('/servicos');
      if (response.data.success) {
        // DIFERENÇA IMPORTANTE:
        // - Tela pública (EmitirSenha.js): usa filtro "ativo && exibir_na_emissao"
        // - Modal do atendente: usa apenas "ativo" (ignora exibir_na_emissao)
        // Isso permite que o atendente emita senhas para qualquer serviço ativo
        const servicosHabilitados = response.data.data.filter(servico => 
          servico.ativo // Apenas filtrar por ativo, ignorar exibir_na_emissao
        );
        
        const servicosComIcones = servicosHabilitados.map(servico => ({
          id: servico.id,
          nome: servico.nome,
          abreviacao: servico.abreviacao,
          icon: getServicoIcon(servico.abreviacao),
          cor: getServicoCor(servico.abreviacao),
        }));
        setServicos(servicosComIcones);
        
        console.log(`📋 Modal atendente: ${servicosComIcones.length} serviços carregados (todos os ativos)`);
      }
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      toast.error('Erro ao carregar serviços disponíveis');
    } finally {
      setCarregandoServicos(false);
    }
  };

  const fetchMotivosEmissao = async () => {
    setCarregandoMotivos(true);
    try {
      const response = await api.get('/motivos-emissao');
      if (response.data.success && response.data.data.length > 0) {
        // Ordenar motivos para garantir que "Outro" sempre seja o último
        const motivosOrdenados = response.data.data.sort((a, b) => {
          const aIsOutro = a.nome.toLowerCase() === 'outro';
          const bIsOutro = b.nome.toLowerCase() === 'outro';
          
          if (aIsOutro && !bIsOutro) return 1; // a vai para o final
          if (!aIsOutro && bIsOutro) return -1; // b vai para o final
          if (aIsOutro && bIsOutro) return 0; // ambos são "outro", manter ordem
          
          // Para os demais, ordenar alfabeticamente
          return a.nome.localeCompare(b.nome);
        });
        
        setMotivosDisponiveis(motivosOrdenados);
        console.log(`📋 Motivos de emissão carregados: ${motivosOrdenados.length}`);
      }
    } catch (error) {
      console.error('Erro ao buscar motivos de emissão:', error);
      // Manter os motivos hardcodados como fallback (já estão na ordem correta)
      console.log('Usando motivos hardcodados como fallback');
    } finally {
      setCarregandoMotivos(false);
    }
  };

  // Função para obter o ícone baseado na abreviação do serviço
  const getServicoIcon = (abreviacao) => {
    const icones = {
      'SV': DescriptionIcon,
      'AP': AssignmentIcon,
      'AE': DescriptionIcon,
      'AT': GavelIcon,
      'ME': PersonIcon,
      'PA': AssignmentIcon,
      'PV': AssignmentIcon,
      'PP': AssignmentIcon,
      'PO': GavelIcon,
      'PE': DescriptionIcon,
      'RA': DescriptionIcon,
    };
    return icones[abreviacao] || DescriptionIcon;
  };

  // Função para obter a cor baseada na abreviação do serviço
  const getServicoCor = (abreviacao) => {
    const cores = {
      'SV': '#4CAF50',
      'AP': '#2196F3',
      'AE': '#9C27B0',
      'AT': '#FF9800',
      'ME': '#F44336',
      'PA': '#3F51B5',
      'PV': '#009688',
      'PP': '#673AB7',
      'PO': '#795548',
      'PE': '#607D8B',
      'RA': '#E91E63',
    };
    return cores[abreviacao] || '#4CAF50';
  };

  // Manipulador para clicar em um serviço
  const handleClicarServico = (servico) => {
    setServicoSelecionado(servico);
    setDialogoAberto(true);
  };

  // Manipulador para fechar o diálogo de seleção de tipo
  const handleFecharDialogo = () => {
    setDialogoAberto(false);
    setServicoSelecionado(null);
  };

  const handleEmitirSenha = async (isPreferencial) => {
    if (!servicoSelecionado || !user) {
      toast.error('Erro: dados insuficientes para emitir senha');
      return;
    }

    // Validar motivo obrigatório
    if (!motivoEmissao) {
      toast.error('Por favor, selecione o motivo da emissão');
      return;
    }

    // Encontrar o motivo selecionado para verificar se requer justificativa
    const motivoSelecionado = motivosDisponiveis.find(m => m.id === motivoEmissao || m.nome === motivoEmissao);
    
    // Validar justificativa se o motivo requer justificativa
    if (motivoSelecionado?.requer_justificativa && !justificativaOutro.trim()) {
      toast.error('Por favor, informe a justificativa para este motivo');
      return;
    }

    setEmitindoSenha(true);
    try {
      const dadosParaEnviar = {
        servico_nome: servicoSelecionado.nome,
        preferencial: isPreferencial,
        // Adicionar informações do usuário para direcionamento
        atendente_id: user.id,
        emitida_por_atendente: true, // Flag para indicar que foi emitida pelo próprio atendente
        sem_vocalizacao: !chamarNoPainel, // CORRIGIDO: Se NÃO deve chamar no painel, então sem_vocalizacao = true
        // Configurações adicionais
        imprimir_senha: imprimirSenha,
        chamar_no_painel: chamarNoPainel,
        motivo_emissao_id: motivoSelecionado?.id || null,
        outro_motivo: motivoSelecionado?.requer_justificativa ? justificativaOutro : null,
      };

      console.log('🔧 Emitindo senha para o próprio atendente com configurações:', {
        ...dadosParaEnviar,
        configuracoes_resumo: {
          imprimir: imprimirSenha ? '✅ SIM' : '❌ NÃO',
          chamar_painel: chamarNoPainel ? '✅ SIM' : '❌ NÃO',
          sem_vocalizacao: !chamarNoPainel ? '✅ SIM (não vocalizar)' : '❌ NÃO (vocalizar)',
          motivo: motivoEmissao,
          justificativa: motivoEmissao === 'outro' ? justificativaOutro : 'N/A'
        }
      });

      const response = await api.post('/senhas/emitir', dadosParaEnviar);
      
      if (response.data.success) {
        const senhaData = response.data.data;

        setSenhaEmitida(senhaData);
        setDialogoAberto(false);
        setDialogoSenhaAberto(true);
        toast.success('Senha emitida com sucesso!');
        
        // Callback para atualizar a fila do dashboard IMEDIATAMENTE
        if (onSenhaEmitida) {
          setAtualizandoDashboard(true);
          toast.info('Atualizando dashboard...', { autoClose: 2000 });
          
          // Aguardar um pequeno delay para garantir que o banco foi atualizado
          setTimeout(async () => {
            try {
              await onSenhaEmitida(senhaData);
              console.log('✅ Dashboard atualizado com sucesso após emissão');
              toast.success('Dashboard atualizado! Sua senha está disponível.', { autoClose: 3000 });
            } catch (error) {
              console.error('❌ Erro ao atualizar dashboard:', error);
              toast.error('Erro ao atualizar dashboard. Atualize a página manualmente.');
            } finally {
              setAtualizandoDashboard(false);
            }
          }, 500);
        }
        
        // Verificar o resultado da impressão
        if (response.data.impressao) {
          if (imprimirSenha) {
            // Se impressão foi solicitada
            if (response.data.impressao.sucesso) {
              toast.success('Senha enviada para impressora');
            } else {
              toast.warning('Não foi possível imprimir a senha. Verifique a impressora.');
            }
          } else {
            // Se impressão não foi solicitada
            toast.info('Senha emitida sem impressão (conforme configurado)');
          }
        }
      } else {
        toast.error('Erro ao emitir senha: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao emitir senha:', error);
      toast.error('Erro ao emitir senha: ' + (error.response?.data?.message || 'Erro desconhecido'));
    } finally {
      setEmitindoSenha(false);
    }
  };

  // Manipulador para fechar o modal principal
  const handleClose = () => {
    setDialogoAberto(false);
    setDialogoSenhaAberto(false);
    setServicoSelecionado(null);
    setSenhaEmitida(null);
    setEmitindoSenha(false);
    setAtualizandoDashboard(false);
    // Resetar configurações
    setImprimirSenha(false); // Padrão: não imprimir
    setChamarNoPainel(false);
    setMotivoEmissao('');
    setJustificativaOutro('');
    onClose();
  };

  return (
    <>
      {/* Modal principal de seleção de serviços */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="xl"
        fullWidth
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: alpha('#000', isDarkMode ? 0.7 : 0.5),
            backdropFilter: 'blur(4px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : 'background.paper',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(0,0,0,0.2)',
            border: isDarkMode 
              ? `1px solid ${alpha('#fff', 0.1)}`
              : 'none',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: isDarkMode ? alpha('#000', 0.4) : alpha('#f5f5f5', 0.5),
          borderRadius: 2,
          border: `1px solid ${alpha(isDarkMode ? '#fff' : '#000', 0.1)}`,
        }}>
          <Box>
            <Typography variant="h5" component="div" sx={{ 
              fontWeight: 600, 
              fontFamily: 'Montserrat',
              background: isDarkMode 
                ? 'linear-gradient(45deg, #90caf9, #42a5f5)' 
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              pt:2,
            }}>
              Emitir Senha - Selecione o Serviço
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              display: 'block',
              mt: 0.5
            }}>
              Como atendente, você pode emitir senhas para todos os serviços ativos
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{
              color: isDarkMode ? 'grey.400' : 'grey.500',
              '&:hover': {
                color: isDarkMode ? 'grey.200' : 'grey.700',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          {/* Configurações na horizontal */}
          <Paper
            elevation={0}
            sx={{
              mb: 0.5,
              p: 2.5,
              borderRadius: 2,
              backgroundColor: isDarkMode ? alpha('#000', 0.2) : alpha('#f5f5f5', 0.5),
              border: `1px solid ${alpha(isDarkMode ? '#fff' : '#000', 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', mb: 0}}>
              <Typography variant="h6" sx={{ mb: 0, fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <SettingsIcon fontSize="small" />
                Configurações
              </Typography>
              
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  display: 'block',
                  textAlign: 'left',
                }}
              >
                * Campo obrigatório
              </Typography>
            </Box>

            <Grid container spacing={0} alignItems="center">
              {/* Switches na primeira linha */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={imprimirSenha}
                      onChange={(e) => setImprimirSenha(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PrintIcon fontSize="small" />
                      <Typography variant="body2">Imprimir senha</Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={chamarNoPainel}
                      onChange={(e) => setChamarNoPainel(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VolumeUpIcon fontSize="small" />
                      <Typography variant="body2">Chamar no painel</Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Select para Motivo da Emissão */}
              <Grid item xs={12} sm={6} md={3} sx={{px: 2}}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="motivo-emissao-label" required>
                    Motivo da emissão 
                  </InputLabel>
                  <Select
                    labelId="motivo-emissao-label"
                    value={motivoEmissao}
                    label="Motivo da emissão "
                    onChange={(e) => {
                      setMotivoEmissao(e.target.value);
                      // Limpar justificativa quando trocar de motivo
                      setJustificativaOutro('');
                    }}
                    required
                    error={!motivoEmissao}
                    size="small"
                    disabled={carregandoMotivos}
                    startAdornment={<EditNoteIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    {motivosDisponiveis.map((motivo) => (
                      <MenuItem key={motivo.id || motivo.nome} value={motivo.id || motivo.nome}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {motivo.nome}
                          {motivo.requer_justificativa && (
                            <Chip 
                              size="small" 
                              label="Requer justificativa" 
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {!motivoEmissao && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        mt: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Selecione um motivo para continuar
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Campo de justificativa quando necessário */}
              {motivosDisponiveis.find(m => (m.id === motivoEmissao || m.nome === motivoEmissao) && m.requer_justificativa) && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Justificativa"
                    placeholder="Descreva o motivo da emissão..."
                    value={justificativaOutro}
                    onChange={(e) => setJustificativaOutro(e.target.value)}
                    multiline
                    rows={1}
                    variant="outlined"
                    required
                    size="small"
                  />
                </Grid>
              )}

            </Grid>
          </Paper>

          {/* Layout de uma coluna - Seleção de serviços */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Selecione o Serviço
          </Typography>
          
          {carregandoServicos ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 4,
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Carregando serviços...
              </Typography>
            </Box>
          ) : servicos.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: alpha(customTheme.palette.grey[500], 0.1),
                  mb: 2,
                }}
              >
                <SettingsIcon sx={{ 
                  fontSize: 40, 
                  color: customTheme.palette.grey[500],
                }} />
              </Box>
              <Typography 
                variant="h6" 
                component="h2" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.secondary',
                  fontFamily: 'Montserrat',
                  mb: 1,
                  textAlign: 'center',
                }}
              >
                Nenhum Serviço Disponível
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  textAlign: 'center',
                  maxWidth: 400,
                  lineHeight: 1.6,
                }}
              >
                Não há serviços habilitados para emissão de senhas no momento.
              </Typography>
              </Box>
            ) : (
              <Grid container spacing={1.5}>
                {servicos.map((servico) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={servico.id}>
                    <Paper
                      elevation={0}
                      onClick={() => handleClicarServico(servico)}
                      sx={{
                        p: 1.5,
                        height: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 2,
                        backgroundColor: isDarkMode ? alpha('#000', 0.4) : 'background.paper',
                        border: `1px solid ${alpha(servico.cor, isDarkMode ? 0.3 : 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 16px ${alpha(servico.cor, isDarkMode ? 0.4 : 0.2)}`,
                          bgcolor: alpha(servico.cor, isDarkMode ? 0.15 : 0.05),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: alpha(servico.cor, isDarkMode ? 0.2 : 0.1),
                          mb: 1,
                          transition: 'all 0.3s ease',
                          boxShadow: isDarkMode 
                            ? `0 0 15px ${alpha(servico.cor, 0.3)}`
                            : 'none',
                        }}
                      >
                        <servico.icon sx={{ 
                          fontSize: 20, 
                          color: servico.cor,
                          filter: isDarkMode ? 'brightness(1.2)' : 'none',
                        }} />
                      </Box>
                      <Typography 
                        variant="body2" 
                        component="h3" 
                        sx={{ 
                          fontWeight: 600, 
                          color: servico.cor,
                          fontFamily: 'Montserrat',
                          textAlign: 'center',
                          fontSize: '0.8rem',
                          lineHeight: 1.2,
                          textShadow: isDarkMode 
                            ? `0 0 8px ${alpha(servico.cor, 0.4)}`
                            : 'none',
                        }}
                      >
                        {servico.nome}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de seleção de tipo de senha */}
      <Dialog
        open={dialogoAberto}
        onClose={handleFecharDialogo}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: alpha('#000', isDarkMode ? 0.8 : 0.6),
            backdropFilter: 'blur(4px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : 'background.paper',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(0,0,0,0.2)',
            border: isDarkMode 
              ? `1px solid ${alpha('#fff', 0.1)}`
              : 'none',
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" component="div" sx={{ 
            fontWeight: 600, 
            fontFamily: 'Montserrat',
            color: isDarkMode ? 'primary.light' : 'text.primary',
          }}>
            Selecione o Tipo de Senha
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleFecharDialogo}
            aria-label="close"
            sx={{
              color: isDarkMode ? 'grey.400' : 'grey.500',
              '&:hover': {
                color: isDarkMode ? 'grey.200' : 'grey.700',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => handleEmitirSenha(false)}
                startIcon={emitindoSenha ? <CircularProgress size={20} /> : <PersonIcon />}
                disabled={emitindoSenha}
                sx={{
                  py: 2.5,
                  borderRadius: 3,
                  borderWidth: 2,
                  fontFamily: 'Montserrat',
                  borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                  color: isDarkMode ? 'primary.light' : 'primary.main',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                    backgroundColor: alpha(customTheme.palette.primary.main, 0.1),
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                {emitindoSenha ? 'Emitindo...' : 'Senha Normal'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleEmitirSenha(true)}
                startIcon={emitindoSenha ? <CircularProgress size={20} /> : <AccessibilityNewIcon />}
                disabled={emitindoSenha}
                sx={{
                  py: 2.5,
                  borderRadius: 3,
                  fontFamily: 'Montserrat',
                  background: isDarkMode
                    ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                    : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  boxShadow: isDarkMode
                    ? '0 4px 20px rgba(144, 202, 249, 0.4)'
                    : '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'linear-gradient(45deg, #82b1ff, #2979ff)'
                      : 'linear-gradient(45deg, #1565c0, #1976d2)',
                    boxShadow: isDarkMode
                      ? '0 6px 24px rgba(144, 202, 249, 0.5)'
                      : '0 6px 16px rgba(25, 118, 210, 0.4)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                {emitindoSenha ? 'Emitindo...' : 'Senha Preferencial'}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Diálogo de senha emitida */}
      <Dialog
        open={dialogoSenhaAberto}
        onClose={() => setDialogoSenhaAberto(false)}
        TransitionComponent={SenhaTransition}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(6px)',
            transition: 'all 0.3s ease-in-out',
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 4,
            backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(144, 202, 249, 0.2)'
              : '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: isDarkMode 
              ? `1px solid ${alpha('#fff', 0.1)}`
              : 'none',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            p: 3,
            background: isDarkMode
              ? `linear-gradient(135deg, ${alpha('#90caf9', 0.1)}, ${alpha('#42a5f5', 0.1)})`
              : `linear-gradient(135deg, ${alpha('#1976d2', 0.05)}, ${alpha('#42a5f5', 0.05)})`,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setDialogoSenhaAberto(false)}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: isDarkMode ? 'grey.400' : 'grey.500',
              bgcolor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.05),
              '&:hover': {
                bgcolor: isDarkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1),
                color: isDarkMode ? 'grey.200' : 'grey.700',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h4"
            component="div"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 4,
              background: isDarkMode
                ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: isDarkMode ? '0 0 30px rgba(144, 202, 249, 0.3)' : 'none',
            }}
          >
            Senha Emitida
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              px: 2,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: '3.5rem',
                mb: 2,
                color: isDarkMode ? 'primary.light' : 'text.primary',
                textShadow: isDarkMode 
                  ? '0 0 30px rgba(144, 202, 249, 0.3)'
                  : '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {senhaEmitida?.senha || ''}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? 'grey.300' : 'text.secondary',
                mb: 2,
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              {senhaEmitida?.servico_nome || ''}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: senhaEmitida?.preferencial 
                  ? isDarkMode ? 'primary.dark' : 'primary.main'
                  : isDarkMode ? alpha('#fff', 0.05) : 'grey.100',
                color: senhaEmitida?.preferencial 
                  ? 'white'
                  : isDarkMode ? 'grey.300' : 'text.primary',
                py: 1,
                px: 3,
                borderRadius: 2,
                boxShadow: isDarkMode && senhaEmitida?.preferencial
                  ? '0 0 20px rgba(144, 202, 249, 0.3)'
                  : 'none',
              }}
            >
              {senhaEmitida?.preferencial ? (
                <AccessibilityNewIcon fontSize="small" />
              ) : (
                <PersonIcon fontSize="small" />
              )}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                }}
              >
                {senhaEmitida?.preferencial ? 'Preferencial' : 'Normal'}
              </Typography>
            </Box>
          </Box>

                      {/* Indicador de atualização */}
            {atualizandoDashboard && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 2,
                  p: 2,
                  bgcolor: isDarkMode ? alpha('#90caf9', 0.1) : alpha('#1976d2', 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(isDarkMode ? '#90caf9' : '#1976d2', 0.2)}`,
                }}
              >
                <CircularProgress size={16} />
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? 'primary.light' : 'primary.main',
                    fontWeight: 500,
                  }}
                >
                  Atualizando dashboard...
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setDialogoSenhaAberto(false)}
                disabled={atualizandoDashboard}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  background: isDarkMode
                    ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                    : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  boxShadow: isDarkMode
                    ? '0 4px 20px rgba(144, 202, 249, 0.4)'
                    : '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'linear-gradient(45deg, #82b1ff, #2979ff)'
                      : 'linear-gradient(45deg, #1565c0, #1976d2)',
                    boxShadow: isDarkMode
                      ? '0 6px 24px rgba(144, 202, 249, 0.5)'
                      : '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {atualizandoDashboard ? 'Aguarde...' : 'Fechar'}
              </Button>
            </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default EmitirSenhaModal; 